import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import {
  Sparkles,
  CheckCheck,
  MapPin,
  AlertTriangle,
  ListChecks,
  RefreshCw,
  Inbox as InboxIcon,
  Filter,
  Loader2,
  Users,
  X,
  Check,
  Phone,
  Mail,
  Building2,
  Target,
  MessageSquare,
} from "lucide-react";

const VERDICT_STYLE = {
  GO: { bg: "rgba(16,185,129,0.12)", color: "#047857", border: "rgba(16,185,129,0.35)" },
  MAYBE: { bg: "rgba(245,158,11,0.12)", color: "#B45309", border: "rgba(245,158,11,0.35)" },
  PASS: { bg: "rgba(231,111,81,0.14)", color: "#B91C1C", border: "rgba(231,111,81,0.35)" },
};

export default function Inbox() {
  const [items, setItems] = useState([]);
  const [verdict, setVerdict] = useState("GO");
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState({});
  const [matchOpen, setMatchOpen] = useState(null); // lead obj or null

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inbox?verdict=${verdict}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [verdict]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const markAllRead = async () => {
    await api.post("/inbox/mark-all-read");
    fetchInbox();
    // also trigger sidebar badge refresh via a window event
    window.dispatchEvent(new CustomEvent("af:inbox-changed"));
  };

  const markRead = async (id) => {
    await api.patch(`/inbox/${id}/read`);
    setItems((arr) => arr.map((l) => (l.id === id ? { ...l, ai_notification_read: true } : l)));
    window.dispatchEvent(new CustomEvent("af:inbox-changed"));
  };

  const reanalyze = async (id) => {
    setReanalyzing((m) => ({ ...m, [id]: true }));
    try {
      await api.post(`/inbox/${id}/reanalyze`);
      // poll briefly for done
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data } = await api.get(`/leads/${id}`);
        if (data.ai_status === "done") {
          fetchInbox();
          break;
        }
      }
    } finally {
      setReanalyzing((m) => ({ ...m, [id]: false }));
    }
  };

  return (
    <div data-testid="inbox-page">
      <div className="topbar">
        <div>
          <div className="crumb">Inbox</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1 flex items-center gap-2">
            Deal-of-the-Day <Sparkles size={22} className="text-[var(--primary)]" />
          </h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">AI runs on every lead. We surface only the ones worth your time.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
            <select
              className="select pl-9 max-w-[180px]"
              value={verdict}
              onChange={(e) => setVerdict(e.target.value)}
              data-testid="inbox-verdict-filter"
            >
              <option value="GO">GO only</option>
              <option value="MAYBE">MAYBE</option>
              <option value="PASS">PASS</option>
              <option value="ALL">All analyzed</option>
            </select>
          </div>
          <button type="button" className="btn-outline" onClick={markAllRead} data-testid="inbox-mark-all-read">
            <CheckCheck size={14} /> Mark all read
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card text-center py-16 text-[var(--ink-2)]" data-testid="inbox-loading">
          <Loader2 size={20} className="animate-spin mx-auto mb-3" /> Loading deals…
        </div>
      ) : items.length === 0 ? (
        <div className="empty" data-testid="inbox-empty">
          <InboxIcon size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-semibold mb-1">No {verdict === "ALL" ? "" : verdict + " "}deals yet</div>
          <div className="text-sm">
            {verdict === "GO"
              ? "Add leads with ARV + repair estimates. We'll auto-analyze and surface the winners here."
              : "Try a different verdict filter, or add more leads."}
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {items.map((l) => (
            <DealCard
              key={l.id}
              lead={l}
              reanalyzing={!!reanalyzing[l.id]}
              onMarkRead={() => markRead(l.id)}
              onReanalyze={() => reanalyze(l.id)}
              onMatchBuyers={() => setMatchOpen(l)}
            />
          ))}
        </div>
      )}

      {matchOpen && <MatchBuyersModal lead={matchOpen} onClose={() => setMatchOpen(null)} />}
    </div>
  );
}

function DealCard({ lead, onMarkRead, onReanalyze, reanalyzing, onMatchBuyers }) {
  const v = lead.ai_verdict;
  const style = VERDICT_STYLE[v] || {};
  const metrics = lead.ai_metrics || {};
  const unread = !lead.ai_notification_read;
  return (
    <div
      className={"card relative overflow-hidden " + (unread ? "ring-2 ring-[var(--primary)] ring-offset-2 ring-offset-[var(--bg)]" : "")}
      data-testid={`deal-card-${lead.id}`}
    >
      {unread && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full text-[var(--primary-dark)]"
          style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)" }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />
          New
        </span>
      )}

      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
            <MapPin size={13} /> {[lead.city, lead.state].filter(Boolean).join(", ") || "—"}
          </div>
          <h3 className="font-display font-bold text-xl mt-1">{lead.property_address}</h3>
          <div className="text-xs text-[var(--ink-3)] mt-1">
            Stage · <span className="text-[var(--ink-2)] font-medium">{lead.stage}</span>
          </div>
        </div>
        {v && (
          <span
            className="chip shrink-0"
            style={{ background: style.bg, color: style.color, borderColor: style.border, fontSize: 13, padding: "6px 14px" }}
          >
            <span className="chip-dot" style={{ background: style.color }} />
            {v}
          </span>
        )}
      </div>

      <p className="text-[15px] leading-relaxed mb-4">{lead.ai_summary}</p>

      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <Pill label="MAO" value={fmtMoney(metrics.mao)} accent />
        <Pill label="Profit" value={fmtMoney(metrics.projected_profit)} accent={metrics.projected_profit > 0} />
        <Pill label="ARV" value={fmtMoney(lead.estimated_arv)} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <MiniList icon={AlertTriangle} title="Risks" items={lead.ai_risks} />
        <MiniList icon={ListChecks} title="Next steps" items={lead.ai_next_steps} />
      </div>

      {/* Auto-matched cash buyers — only shown for GO verdicts */}
      {v === "GO" && (lead.top_matches?.length > 0 ? (
        <MatchStrip lead={lead} onOpen={onMatchBuyers} />
      ) : (
        <div
          className="mt-4 rounded-lg border border-dashed p-3 flex items-center justify-between gap-3 text-sm"
          style={{ borderColor: "var(--line)", background: "rgba(15,23,42,0.025)" }}
          data-testid={`deal-no-matches-${lead.id}`}
        >
          <span className="text-[var(--ink-2)] flex items-center gap-2">
            <Target size={13} className="text-[var(--ink-3)]" />
            No Cash Buyers match this deal yet.
          </span>
          <Link to="/app/team" className="btn-ghost !py-1 !px-2 text-xs">Add buyer →</Link>
        </div>
      ))}

      <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-[var(--line)] flex-wrap">
        <Link to={`/app/leads/${lead.id}/report`} className="btn-ghost" data-testid={`deal-report-${lead.id}`}>
          Full report →
        </Link>
        {v === "GO" && !lead.top_matches?.length && (
          <button type="button" className="btn-money !py-2 !px-3 text-sm" onClick={onMatchBuyers} data-testid={`deal-match-${lead.id}`}>
            <Users size={13} /> Match buyers
          </button>
        )}
        <button type="button" className="btn-ghost" onClick={onReanalyze} disabled={reanalyzing} data-testid={`deal-reanalyze-${lead.id}`}>
          {reanalyzing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {reanalyzing ? "Re-analyzing" : "Re-analyze"}
        </button>
        {unread && (
          <button type="button" className="btn-outline" onClick={onMarkRead} data-testid={`deal-mark-read-${lead.id}`}>
            <CheckCheck size={13} /> Mark read
          </button>
        )}
      </div>
    </div>
  );
}

function MatchBuyersModal({ lead, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/leads/${lead.id}/buyer-matches`).then((r) => {
      if (alive) { setData(r.data); setLoading(false); }
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lead.id]);

  const top = data?.matches || [];
  const dealLine = encodeURIComponent(
    `Hey {{name}}, found a deal that fits your buy box: ${lead.property_address}` +
    (lead.city ? `, ${lead.city}` : "") + (lead.state ? `, ${lead.state}` : "") +
    `. ARV ~$${(lead.estimated_arv || 0).toLocaleString()}, asking $${(lead.asking_price || 0).toLocaleString()}. Want details?`
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="match-buyers-modal">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div>
            <div className="label-eyebrow flex items-center gap-1.5">
              <Sparkles size={11} className="text-[var(--primary)]" /> Ranked by buy-box fit
            </div>
            <h2 className="font-display font-extrabold text-xl mt-1">Match buyers — {lead.property_address}</h2>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="match-modal-close"><X size={16} /></button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12 text-[var(--ink-2)]">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Ranking your buyers…
            </div>
          ) : top.length === 0 ? (
            <div className="empty">
              <Users size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
              <div className="font-semibold mb-1">No cash buyers in your Power Team yet</div>
              <div className="text-sm">Add buyers (with a buy box) to start auto-matching deals.</div>
            </div>
          ) : (
            <div className="flex flex-col gap-3" data-testid="match-results">
              {top.map((b, i) => (
                <BuyerRow key={b.id} buyer={b} rank={i + 1} dealLine={dealLine} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BuyerRow({ buyer, rank, dealLine }) {
  const score = buyer.match_score || 0;
  const tone =
    score >= 80 ? { bg: "rgba(16,185,129,0.12)", color: "#047857", border: "rgba(16,185,129,0.35)", label: "Strong fit" } :
    score >= 50 ? { bg: "rgba(245,158,11,0.12)", color: "#B45309", border: "rgba(245,158,11,0.35)", label: "Possible" } :
                  { bg: "rgba(231,111,81,0.12)", color: "#B91C1C", border: "rgba(231,111,81,0.35)", label: "Weak" };
  const mailto = buyer.email
    ? `mailto:${buyer.email}?subject=${encodeURIComponent("Off-market deal in your buy box")}&body=${dealLine.replace("%7B%7Bname%7D%7D", encodeURIComponent(buyer.name.split(" ")[0]))}`
    : null;
  return (
    <div className="card flex flex-col md:flex-row md:items-center gap-4" data-testid={`match-row-${buyer.id}`}>
      <div className="flex items-start gap-3 md:w-72 shrink-0">
        <div className="flex flex-col items-center justify-center w-10 shrink-0">
          <span className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-bold">#</span>
          <span className="font-display font-black text-2xl tabular-nums">{rank}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg leading-tight truncate">{buyer.name}</div>
          <div className="text-xs text-[var(--ink-2)] flex items-center gap-1 mt-0.5"><Building2 size={11} /> {buyer.company || "—"}</div>
          <span className="chip mt-2" style={{ background: tone.bg, color: tone.color, borderColor: tone.border, fontSize: 11 }}>
            {tone.label} · {score}
          </span>
        </div>
      </div>
      <ul className="flex-1 space-y-1.5 text-sm">
        {(buyer.match_reasons || []).map((r, i) => (
          <li key={`${r.label}-${i}`} className="flex items-start gap-2">
            {r.ok === true ? <Check size={14} className="mt-0.5 text-[var(--primary)] shrink-0" /> :
             r.ok === false ? <X size={14} className="mt-0.5 text-[var(--warn)] shrink-0" /> :
                              <span className="mt-1 w-2 h-2 rounded-full bg-[var(--ink-3)] shrink-0" />}
            <span className={r.ok === false ? "text-[var(--ink-2)]" : ""}>{r.label}</span>
          </li>
        ))}
      </ul>
      <div className="flex md:flex-col gap-2 md:w-32 shrink-0">
        {buyer.phone && (
          <a href={`tel:${buyer.phone}`} className="btn-outline justify-center flex-1" data-testid={`match-call-${buyer.id}`}>
            <Phone size={13} /> Call
          </a>
        )}
        {mailto && (
          <a href={mailto} className="btn-money justify-center flex-1" data-testid={`match-email-${buyer.id}`}>
            <Mail size={13} /> Email
          </a>
        )}
      </div>
    </div>
  );
}

function Pill({ label, value, accent }) {
  return (
    <div className="card-flat p-2.5">
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-semibold">{label}</div>
      <div className={"font-display font-bold text-base mt-0.5 tabular-nums " + (accent ? "text-[var(--primary)]" : "")}>{value}</div>
    </div>
  );
}

function MatchStrip({ lead, onOpen }) {
  const matches = lead.top_matches || [];
  const count = lead.top_match_count || matches.length;
  const top = matches.slice(0, 3);
  const [sentMap, setSentMap] = useState({});

  // Hydrate "already sent" indicators on mount so refreshes don't lose the badge
  useEffect(() => {
    let alive = true;
    api.get(`/leads/${lead.id}/dispo/summary`).then((r) => {
      if (!alive) return;
      const by = r.data?.by_buyer || {};
      const next = {};
      Object.entries(by).forEach(([bid, info]) => {
        if (["sent", "replied", "won"].includes(info.kind)) next[bid] = info.kind;
      });
      setSentMap(next);
    }).catch(() => {});
    return () => { alive = false; };
  }, [lead.id]);

  const logDispo = async (buyer, channel) => {
    setSentMap((m) => ({ ...m, [buyer.id]: "sent" }));
    try {
      await api.post(`/leads/${lead.id}/dispo`, {
        kind: "sent", buyer_id: buyer.id, buyer_name: buyer.name, channel,
      });
    } catch { /* ignore — sms: / tel: link already fired */ }
  };

  const smsBody = (m) => {
    const first = (m.name || "").split(" ")[0] || "";
    return encodeURIComponent(
      `Hey ${first}, off-market deal in your buy box: ${lead.property_address}` +
      (lead.city ? `, ${lead.city}` : "") +
      `. ARV ~$${(lead.estimated_arv || 0).toLocaleString()}, asking $${(lead.asking_price || 0).toLocaleString()}. Want details?`
    );
  };
  return (
    <div
      className="mt-4 rounded-lg p-3 border"
      style={{
        background: "linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(14,165,233,0.06) 100%)",
        borderColor: "rgba(16,185,129,0.3)",
      }}
      data-testid={`deal-matches-${lead.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--primary-dark)] hover:underline"
          data-testid={`deal-matches-open-${lead.id}`}
        >
          <Target size={14} />
          🎯 {count} buyer{count === 1 ? "" : "s"} match{count === 1 ? "es" : ""} this deal
        </button>
        <button type="button" className="text-xs text-[var(--ink-3)] hover:text-[var(--ink)]" onClick={onOpen}>
          See all →
        </button>
      </div>
      <ul className="space-y-1.5" data-testid={`deal-matches-list-${lead.id}`}>
        {top.map((m) => {
          const status = sentMap[m.id];
          return (
            <li
              key={m.id}
              className="flex items-center gap-3 rounded-md px-2 py-1.5 bg-white/65 border border-[var(--line)]"
              data-testid={`deal-match-row-${lead.id}-${m.id}`}
            >
              <div className="font-mono font-bold tabular-nums text-sm w-10 text-center shrink-0"
                   style={{ color: m.score >= 80 ? "#047857" : m.score >= 50 ? "#B45309" : "#64748B" }}>
                {m.score}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                  {m.name}
                  {status && (
                    <span
                      className="chip"
                      style={{
                        background: status === "won" ? "#10B98122" : status === "replied" ? "#F59E0B22" : "#0EA5E922",
                        color: status === "won" ? "#10B981" : status === "replied" ? "#F59E0B" : "#0EA5E9",
                        borderColor: (status === "won" ? "#10B981" : status === "replied" ? "#F59E0B" : "#0EA5E9") + "55",
                        fontSize: 9,
                      }}
                      data-testid={`deal-match-status-${m.id}`}
                    >
                      <span className="chip-dot" style={{ background: status === "won" ? "#10B981" : status === "replied" ? "#F59E0B" : "#0EA5E9" }} />
                      {status === "won" ? "won" : status === "replied" ? "replied" : "sent"}
                    </span>
                  )}
                </div>
                {m.company && <div className="text-xs text-[var(--ink-3)] truncate">{m.company}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {m.phone && (
                  <a
                    href={`sms:${m.phone}?body=${smsBody(m)}`}
                    onClick={() => logDispo(m, "sms")}
                    className="btn-money !py-1 !px-2 text-xs"
                    data-testid={`deal-match-sms-${m.id}`}
                    title="Text this buyer"
                  >
                    <MessageSquare size={12} /> Text
                  </a>
                )}
                {m.phone && (
                  <a
                    href={`tel:${m.phone}`}
                    onClick={() => logDispo(m, "call")}
                    className="btn-outline !py-1 !px-2 text-xs"
                    data-testid={`deal-match-call-${m.id}`}
                    title="Call this buyer"
                  >
                    <Phone size={12} />
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function MiniList({ icon: Icon, title, items }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={13} className="text-[var(--ink-2)]" />
        <span className="font-semibold text-xs uppercase tracking-widest text-[var(--ink-2)]">{title}</span>
      </div>
      <ul className="space-y-1.5">
        {(items || []).slice(0, 3).map((s, i) => (
          <li key={`${s}-${i}`} className="text-[13px] leading-relaxed flex items-start gap-2">
            <span className="text-[var(--primary)] font-mono mt-0.5">›</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
